/**
 * SyncJobsTab Component
 *
 * Tab content for the Sync Jobs section of the Management page.
 * Displays sync status cards for each source with auto-refresh.
 */

import { SyncJobCard } from './SyncJobCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SyncJob, SyncSource } from '@/types/sync';

interface SyncJobsTabProps {
  syncs: SyncJob[];
  isLoading: boolean;
  error: string | null;
  startSync: (source: SyncSource) => Promise<void>;
  refetch: () => void;
  lastUpdated: Date | null;
}

export function SyncJobsTab({
  syncs,
  isLoading,
  error,
  startSync,
  refetch,
  lastUpdated,
}: SyncJobsTabProps) {
  const handleStartSync = async (source: SyncSource) => {
    await startSync(source);
  };

  // Show error state first (takes precedence)
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Error</AlertTitle>
        <AlertDescription className="flex items-center justify-between">
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={refetch}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  // Show skeleton while loading OR before first successful fetch completes
  // lastUpdated is only set after a successful fetch, so if it's null, we're still waiting
  if (isLoading || (syncs.length === 0 && !lastUpdated)) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {/* Skeleton mimics SyncJobCard layout */}
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl border bg-card p-6 space-y-4">
            {/* Header: title + badge */}
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-4 w-40" />
              </div>
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            {/* Metrics grid */}
            <div className="grid grid-cols-3 gap-4">
              {[0, 1, 2].map((j) => (
                <div key={j} className="space-y-1">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-5 w-8" />
                </div>
              ))}
            </div>
            {/* Timestamp */}
            <Skeleton className="h-4 w-32" />
            {/* Button */}
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
    );
  }

  // Sort syncs in consistent order: Fathom, HelpScout, Basecamp
  const sourceOrder = ['fathom', 'helpscout', 'basecamp'];
  const sortedSyncs = [...syncs].sort((a, b) => {
    return sourceOrder.indexOf(a.source) - sourceOrder.indexOf(b.source);
  });

  // Show empty state only after we've confirmed there's no data (lastUpdated is set)
  if (syncs.length === 0) {
    return (
      <EmptyState
        icon={RefreshCw}
        title="No sync sources configured"
        description="Sync jobs will appear here once data sources are connected to your account."
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Status bar */}
      {lastUpdated && (
        <div className="flex items-center justify-end text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
            <span>Updated {formatRelativeTime(lastUpdated)}</span>
          </div>
        </div>
      )}

      {/* Sync cards grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {sortedSyncs.map((sync) => (
          <SyncJobCard
            key={sync.source}
            sync={sync}
            onStartSync={handleStartSync}
          />
        ))}
      </div>
    </div>
  );
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 5) return 'just now';
  if (diffSecs < 60) return `${diffSecs}s ago`;
  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  return `${diffHours}h ago`;
}
